<template>
    <div fluid class="query-fields-view">
        <v-row dense>
            <v-col cols="2">
                <div style="display: flex;">
                    <v-container>
                        <timeline
                            class="fullscreen-timeline scrollbar-custom"
                            :items="items"
                            :selectedItem="selectedModelisation"
                            @selected-item="switchModelisation"
                        ></timeline>
                    </v-container>
                    <v-divider :vertical="true"></v-divider>
                </div>
            </v-col>
            <v-col cols="10"  class="right-part">
                <v-container fluid>
                    <v-alert
                        :value="isReadOnly"
                        type="info"
                    >
                        {{$t('instance.query_fields.index.cant_edit_queries_text')}}
                    </v-alert>
                    <div>
                        <b>{{$t('instance.query_fields.index.define_queries_text')}} </b> {{$t('instance.query_fields.index.define_queries_text_description')}}
                    </div>
                    <br>
                    <edit-resource-modal
                        ref="dialog"
                        @on-resource-query-saved="saveResourceQuery"
                    ></edit-resource-modal>
                    <v-data-table
                        :headers="resourceTableHeader"
                        :items="resourceTableItems"
                        hide-default-header
                        :options.sync="paginationOptions"
                        :footer-props="{
                            showFirstLastPage: true,
                            itemsPerPageText:$t('instance.permissions.pagination_text'),
                            itemsPerPageOptions:paginationOptions.itemsRange,
                        }"
                    >
                        <template v-slot:header="{ props: { headers } }">
                            <thead>
                                <tr>
                                    <th
                                        v-for="header in headers"
                                        :key="header.value"
                                    >
                                        <v-tooltip
                                            top
                                            v-if="!['name','edit'].includes(header.value)"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <span v-on="on">{{header.text}}</span>
                                                <v-icon small dark v-on="on" color="#9e9e9e">info</v-icon>
                                            </template>
                                            <span style="font-size:14px">{{header.tooltip}}</span>
                                        </v-tooltip>
                                        <span
                                            v-else
                                        >
                                            {{header.text}}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                        </template>
                        <template slot="item" slot-scope="props">
                            <tr>
                                <td>{{ props.item.model_name }}</td>
                                <td>
                                    <model-fields-display :fields="props.item.search_fields">
                                    </model-fields-display>
                                </td>
                                <td>
                                    <model-fields-display :fields="props.item.filter_fields">
                                    </model-fields-display>
                                </td>
                                <td>
                                    <model-fields-display :fields="props.item.display_fields">
                                    </model-fields-display>
                                </td>
                                <td>
                                    <model-fields-display :fields="props.item.export_fields">
                                    </model-fields-display>
                                </td>
                                <td>
                                    <model-fields-display :fields="props.item.ordering_fields">
                                    </model-fields-display>
                                </td>
                                <td class="text-center">
                                    <v-btn
                                        text
                                        icon
                                        :disabled="isReadOnly"
                                        @click="openDialog(props.item)"
                                    >
                                        <v-icon
                                            small
                                            class="edit-icon"
                                        >
                                            edit
                                        </v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </template>
                        <template v-slot:[`footer.page-text`]="items">
                          {{ items.pageStart }} - {{ items.pageStop }} {{ $t('instance.permissions.pagination_and') }} {{ items.itemsLength }}
                        </template>
                    </v-data-table>
                </v-container>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import EditResourceModal from './edit-modal.vue';
import ModelFieldsDisplay from './model-fields-display.vue';
import Timeline from '@/components/Partials/timeline.vue';
import moment from 'moment';
import { cloneDeep } from 'lodash';

export default {

    components: {
        EditResourceModal,
        ModelFieldsDisplay,
        Timeline,
    },

    props: {
        modelisations: {
            type: Array,
            required: true,
        },
        schema: {
            type: Object,
            required: true,
        },
        selectedModelisation:{
            type: Object,
        }
    },

    data (){
        return {
            paginationOptions:{
                page: 1,
                itemsPerPage: 10,
                itemsRange:[
                    5,
                    10,
                    25,
                    {
                        'text':this.$t('instance.query_fields.index.pagination_all'),
                        'value':-1
                    }
                ],
            },
        };
    },

    computed: {
        currentModelisation (){
            return this.modelisations.find((modelisation) => modelisation.editable === true);
        },
        resourceTableHeader (){
            return [
                { 
                    text: `${this.$t('instance.query_fields.index.resourceTableHeader_model')}`, 
                    value: 'name' , 
                    sortable: false, 
                },
                { 
                    text: `${this.$t('instance.query_fields.index.resourceTableHeader_search')}`, 
                    value: 'search' , 
                    sortable: false,
                    tooltip:`${this.$t('instance.query_fields.index.tooltip.search')}`,
                },
                { 
                    text: `${this.$t('instance.query_fields.index.resourceTableHeader_filter')}`, 
                    value: 'filter' , 
                    sortable: false,
                    tooltip:`${this.$t('instance.query_fields.index.tooltip.filter')}`,
                },
                { 
                    text: `${this.$t('instance.query_fields.index.resourceTableHeader_display')}`, 
                    value: 'display' , 
                    sortable: false,
                    tooltip:`${this.$t('instance.query_fields.index.tooltip.display')}`,
                },
                { 
                    text: `${this.$t('instance.query_fields.index.resourceTableHeader_export')}`, 
                    value: 'export' , 
                    sortable: false,
                    tooltip:`${this.$t('instance.query_fields.index.tooltip.export')}`,
                },
                { 
                    text: `${this.$t('instance.query_fields.index.resourceTableHeader_ordering')}`, 
                    value: 'ordering' , 
                    sortable: false,
                    tooltip:`${this.$t('instance.query_fields.index.tooltip.ordering')}`,
                },
                { 
                    text: `${this.$t('instance.query_fields.index.resourceTableHeader_edit_fields')}`, 
                    value: 'edit', 
                    sortable: false,
                }
            ];
        },
        resourceTableItems (){
            return this.schema.resourceQueriesList;
        },
        isReadOnly() {
            return this.schema.version !== this.currentModelisation.version;
        },
        items (){
            let cloneModelisation = cloneDeep(this.modelisations);
            return cloneModelisation.sort((a,b) => new Date(b.creation_date)-new Date(a.creation_date))
                .map((model) => {
                    return {
                        title: `v${model.version}`,
                        text: moment(model.creation_date).format('YYYY/MM/DD'),
                        uid: model.uid,
                        readOnly: !model.editable,
                    };
                });
        },
    },

    mounted (){
    },

    methods: {
        openDialog (resourceQuery){
            let model = this.schema.modelList.find((model) => model.uid === resourceQuery.model_uid);
            this.$refs.dialog.open(resourceQuery, model, this.schema);
        },
        saveResourceQuery (resourceQueryToSave){
            this.$store.commit('SET_SAVE_STATE', 'sync');
            let resourceQueries = this.schema.resourceQueriesList;
            let modifiedResourceQueryIndex = resourceQueries.findIndex((rq) => rq.model_uid === resourceQueryToSave.model_uid);
            resourceQueries.splice(modifiedResourceQueryIndex, 1, resourceQueryToSave);
            this.$store.dispatch('patchItem', {
                item: {
                    url: this.currentModelisation.url,
                    value: this.schema.schema,
                },
                model: 'modelisations'
            }).then(() => {
                this.$store.commit('SET_SNACKBAR', {
                    color: 'success',
                    icon: 'done',
                    text: `${this.$t('instance.query_fields.index.saveResourceQuery_success',{ressource : resourceQueryToSave.model_name})}`,
                });
                this.$store.commit('SET_SAVE_STATE', 'upToDate');
                this.$store.commit('SET_DISPLAY_SNACKBAR', true);
                this.schema.commit();
                this.$refs.dialog.close();
            }).catch(() => {
                this.$store.commit('SET_SNACKBAR', {
                    color: 'error',
                    icon: 'error',
                    text: `${this.$t('instance.query_fields.index.saveResourceQuery_error', {ressource : resourceQueryToSave.model_name})}`
                });
                this.$store.commit('SET_SAVE_STATE', 'error');
                this.$store.commit('SET_DISPLAY_SNACKBAR', true);

            });
        },
        switchModelisation (modelisation){
            let relatedModelisation = this.modelisations.find((_modelisation) => _modelisation.uid === modelisation.uid);
            this.$emit('update-schema-modelisation', relatedModelisation.value, relatedModelisation.version);
        },
        getSlotName(value) {
            console.log(`header.${value}`)
            return `header.${value}`;
        }
    },
};
</script>

<style lang="scss">
.query-fields-view{
    /* Environt la taille de la top-bar + le tabs (instances, data models ,...)*/
    height: calc(100vh - 190px);

    .left-part{
        height: 100%;
    }
    .right-part{
        padding: 1rem;
    }
    .v-table__overflow th, .v-table__overflow td,{
        max-width: 16%;
        word-break: break-all;
        line-height: 16px;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/page/Instance/QueryFields/index.vue